import React, { useState, useEffect, useRef } from 'react';
import { Container, Alert, Spinner } from 'react-bootstrap';
import { isOnOrAfterUtcDay } from '../../utils/utcDateTime';
import Sidebar from './Sidebar';
import Header from './Header';
import ProfileTable from './ProfileTable';
import ProfileDetailModal from './ProfileDetailModal';
import ArchivePrimaryWalletModal from './ArchivePrimaryWalletModal';
import { getKycProfiles } from '../../apis/checkin.api';
import { useAuth } from '../../contexts/auth.context';
import { canArchiveKycPrimaryWallet } from '../../utils/constants';

const ProfileManagement = () => {
  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    profileTypes: ['primary', 'secondary'],
    status: ['COMPLETED', 'NEEDS_REVIEW', 'DECLINED'],
  });
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [notice, setNotice] = useState(null);
  const canArchive = canArchiveKycPrimaryWallet(auth?.username);

  const fetchProfiles = async authToken => {
    try {
      setLoading(true);
      setError(null);
      const data = await getKycProfiles(authToken.current);
      // Sort profiles by modifiedAt in descending order (newest first)
      const sortedData = [...data].sort((a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt));
      setProfiles(sortedData);
    } catch (err) {
      setError('Failed to fetch profiles. Please try again later.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles(authToken);
  }, [authToken]);

  const handleFilterChange = newFilters => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleRefresh = () => {
    fetchProfiles(authToken);
  };

  const handleViewProfile = profile => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };

  const getFilteredProfiles = () => {
    return profiles.filter(profile => {
      // Filter by search term
      const searchTerm = filters.search.toLowerCase();
      if (
        searchTerm &&
        !profile.id.toLowerCase().includes(searchTerm) &&
        !profile.walletId.toLowerCase().includes(searchTerm) &&
        !profile.primaryWalletId.toLowerCase().includes(searchTerm)
      ) {
        return false;
      }

      // Filter by profile type
      const profileType = profile.isPrimary ? 'primary' : 'secondary';
      if (!filters.profileTypes.includes(profileType)) {
        return false;
      }

      // Filter by status
      if (!filters.status.includes(profile.status)) {
        return false;
      }

      if (!isOnOrAfterUtcDay(profile.createdAt, filters.startDate)) {
        return false;
      }

      return true;
    });
  };

  if (loading) {
    return (
      <Container fluid className="h-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  const filteredProfiles = getFilteredProfiles();

  return (
    <Container fluid className="h-100 ProfileManagement">
      {error && (
        <Alert variant="danger" className="mb-0">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          variant={notice.variant}
          className="mb-0"
          onClose={() => setNotice(null)}
          dismissible
        >
          {notice.message}
        </Alert>
      )}
      <Header onRefresh={handleRefresh} profiles={filteredProfiles} />
      <Sidebar filters={filters} onFilterChange={handleFilterChange} />
      <ProfileTable
        profiles={filteredProfiles}
        onViewProfile={handleViewProfile}
        canArchive={canArchive}
        onArchive={setArchiveTarget}
      />
      <ProfileDetailModal
        show={showDetailModal}
        profile={selectedProfile}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedProfile(null);
        }}
        canArchive={canArchive}
        onArchive={setArchiveTarget}
      />
      <ArchivePrimaryWalletModal
        show={Boolean(archiveTarget)}
        profile={archiveTarget}
        authToken={auth?.idToken ?? ''}
        onHide={() => setArchiveTarget(null)}
        onArchived={profile => {
          setArchiveTarget(null);
          setShowDetailModal(false);
          setSelectedProfile(null);
          setNotice({
            variant: 'success',
            message: `Archived ${profile.walletId}. The profile is kept and can be restored.`,
          });
          fetchProfiles(authToken);
        }}
      />
    </Container>
  );
};

export default ProfileManagement;
